import React from 'react';
import { POS_GOLD } from '../../../../../config/posRoomStatus';
import CalendarFilterBar from './CalendarFilterBar';
import CalendarLegend from './CalendarLegend';
import { ReminderChips, useCalendarReminders } from './CalendarReminders';
import { PRIMARY_COLOR, STATUS_CONFIG, BLOCK_STYLE } from './constants';
import { useConfig } from '../../../../../contexts/ConfigContext';
import { formatLocalDate, getInitials, getTerritoryCode, getBlocksForDate } from './helpers';

interface CalendarFilters {
  search: string;
  status: string;
  territory_id: string;
  assigned_to: string;
  context: 'all' | 'pos' | 'cmb';
  show_blocks: boolean;
  show_reminders: boolean;
}

interface FilterOption {
  value: string;
  label: string;
}

interface CalendarBookingRow {
  id: string;
  booking_date_sast: string;
  start_time: string;
  end_time?: string;
  customer_name?: string;
  territory_name?: string;
  status: string;
  operator_names?: string[];
  booking_reference?: string;
  is_pos?: boolean;
  [key: string]: unknown;
}

interface OperatorBlockRow {
  id: string;
  operator_id: string | null;
  reason?: string;
  operator_name?: string;
  start_date: string;
  end_date: string;
  start_time?: string | null;
  end_time?: string | null;
  is_all_day?: boolean;
  [key: string]: unknown;
}

interface MonthViewProps {
  currentMonth: Date;
  setSelectedDate: React.Dispatch<React.SetStateAction<Date>>;
  setViewMode: React.Dispatch<React.SetStateAction<string>>;
  isMobile: boolean;
  calendarFilters: CalendarFilters;
  setCalendarFilters: React.Dispatch<React.SetStateAction<CalendarFilters>>;
  calendarFiltersExpanded: boolean;
  setCalendarFiltersExpanded: React.Dispatch<React.SetStateAction<boolean>>;
  activeCalendarFilterCount: number;
  statusOptions: FilterOption[];
  territoryOptions: FilterOption[];
  operatorOptions: FilterOption[];
  filteredCalendarBookings: CalendarBookingRow[];
  showBlocks: boolean;
  operatorBlocks: OperatorBlockRow[];
  handleViewBooking: (booking: CalendarBookingRow) => void;
  handlePrevDay: () => void;
  handlePrevWeek: () => void;
  handlePrevMonth: () => void;
  handleNextDay: () => void;
  handleNextWeek: () => void;
  handleNextMonth: () => void;
  handleToday: () => void;
  selectedDate: Date;
  viewMode: string;
}

/**
 * Month calendar view - shows month grid with booking indicators
 */
export default function MonthView({
  currentMonth,
  setSelectedDate,
  setViewMode,
  isMobile,
  calendarFilters,
  setCalendarFilters,
  calendarFiltersExpanded,
  setCalendarFiltersExpanded,
  activeCalendarFilterCount,
  statusOptions,
  territoryOptions,
  operatorOptions,
  filteredCalendarBookings,
  showBlocks,
  operatorBlocks,
  handleViewBooking,
  // Navigation
  handlePrevDay,
  handlePrevWeek,
  handlePrevMonth,
  handleNextDay,
  handleNextWeek,
  handleNextMonth,
  handleToday,
  selectedDate,
  viewMode
}: MonthViewProps) {
  const reminders = useCalendarReminders(calendarFilters);
  const year = currentMonth.getFullYear();
  const { config } = useConfig();
  const month = currentMonth.getMonth();
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const startPad = firstDay.getDay(); // 0 = Sunday
  const daysInMonth = lastDay.getDate();
  // Country-local "today" so travelling super-admins see country's clock
  const todayStr = formatLocalDate(new Date(), config?.timezone);

  // Get bookings for a specific date
  const getBookingsForDate = (dateStr: string): CalendarBookingRow[] => {
    return filteredCalendarBookings.filter(b => {
      if (!b.booking_date_sast) return false;
      return b.booking_date_sast === dateStr;
    });
  };

  return (
    <div className="card">
      {/* Shared Filter Bar with integrated date navigation */}
      <CalendarFilterBar
        calendarFilters={calendarFilters}
        setCalendarFilters={setCalendarFilters}
        calendarFiltersExpanded={calendarFiltersExpanded}
        setCalendarFiltersExpanded={setCalendarFiltersExpanded}
        activeCalendarFilterCount={activeCalendarFilterCount}
        statusOptions={statusOptions}
        territoryOptions={territoryOptions}
        operatorOptions={operatorOptions}
        filteredCalendarBookings={filteredCalendarBookings}
        showBlocks={showBlocks}
        viewMode={viewMode as 'day' | 'week' | 'month' | 'list'}
        isMobile={isMobile}
        handlePrevDay={handlePrevDay}
        handlePrevWeek={handlePrevWeek}
        handlePrevMonth={handlePrevMonth}
        handleNextDay={handleNextDay}
        handleNextWeek={handleNextWeek}
        handleNextMonth={handleNextMonth}
        handleToday={handleToday}
        selectedDate={selectedDate}
        currentMonth={currentMonth}
      />

      {/* Calendar Grid */}
      <div className="card-body" style={{ padding: 0 }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, 1fr)',
          borderTop: '1px solid var(--color-border)'
        }}>
          {/* Day headers */}
          {(isMobile ? ['S', 'M', 'T', 'W', 'T', 'F', 'S'] : ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']).map((day, i) => (
            <div key={i} className={`cal-day-header ${isMobile ? 'cal-day-header--mobile' : ''}`}>
              {day}
            </div>
          ))}

          {/* Empty cells for start padding */}
          {Array.from({ length: startPad }).map((_, i) => (
            <div
              key={`pad-${i}`}
              className="cal-cell cal-cell--pad"
              style={{ minHeight: isMobile ? '48px' : '100px' }}
            />
          ))}

          {/* Calendar days */}
          {Array.from({ length: daysInMonth }).map((_, i) => {
            const day = i + 1;
            const cellDate = new Date(year, month, day);
            const dateStr = formatLocalDate(cellDate, config?.timezone);
            const dayBookings = getBookingsForDate(dateStr);
            const dayBlocks = getBlocksForDate(dateStr, showBlocks, operatorBlocks, calendarFilters);
            const dayReminders = reminders.forDate(dateStr);
            const isToday = dateStr === todayStr;
            const isPast = dateStr < todayStr;

            // Mobile: Tap to drill down to day view
            const handleDayClick = () => {
              if (isMobile && (dayBookings.length > 0 || dayBlocks.length > 0 || dayReminders.length > 0)) {
                setSelectedDate(cellDate);
                setViewMode('day');
              }
            };

            return (
              <div
                key={day}
                onClick={handleDayClick}
                className="cal-cell"
                style={{
                  minHeight: isMobile ? '48px' : '100px',
                  padding: isMobile ? '4px' : '8px',
                  background: isToday ? `${PRIMARY_COLOR}08` : isPast ? 'var(--color-bg-secondary)' : '#fff',
                  cursor: isMobile && (dayBookings.length > 0 || dayBlocks.length > 0 || dayReminders.length > 0) ? 'pointer' : undefined
                }}
              >
                {/* Day number */}
                <div style={{
                  fontWeight: isToday ? 700 : 400,
                  fontSize: isMobile ? '0.75rem' : '0.875rem',
                  color: isToday ? PRIMARY_COLOR : isPast ? 'var(--color-text-light)' : 'inherit',
                  marginBottom: isMobile ? '2px' : '4px'
                }}>
                  {day}
                </div>

                {/* Bookings and Blocks for this day */}
                {isMobile ? (
                  // Mobile: Show compact chips
                  (dayBookings.length > 0 || dayBlocks.length > 0 || dayReminders.length > 0) && (
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '2px'
                    }}>
                      <ReminderChips reminders={dayReminders} compact />
                      {/* Block chips first - orange color for blocks */}
                      {dayBlocks.slice(0, 1).map(block => (
                        <div
                          key={`block-${block.id}`}
                          style={{
                            height: '4px',
                            background: BLOCK_STYLE.border,
                            borderRadius: 'var(--radius-sm)'
                          }}
                          title={`Blocked: ${block.reason || 'No reason'} - ${block.operator_name}`}
                        />
                      ))}
                      {/* Booking chips — brand colour (CMB violet / POS gold) is the
                           dominant left accent, status colour fills the trailing portion.
                           Brand share bumped to ~16px so POS gold / CMB violet is unmissable
                           at this tiny scale. */}
                      {dayBookings.slice(0, dayBlocks.length > 0 ? 1 : 2).map((booking: CalendarBookingRow) => {
                        const status = (STATUS_CONFIG[booking.status] || STATUS_CONFIG.pending)!;
                        const brandColour = booking.is_pos ? POS_GOLD : 'var(--color-primary)';
                        return (
                          <div
                            key={booking.id}
                            style={{
                              height: '4px',
                              background: `linear-gradient(to right, ${brandColour} 0, ${brandColour} 32px, ${status.color} 32px, ${status.color} 100%)`,
                              borderRadius: 'var(--radius-sm)'
                            }}
                            title={`${booking.start_time} - ${booking.customer_name}${booking.is_pos ? ' [POS]' : ''}`}
                          />
                        );
                      })}
                      {(dayBookings.length + dayBlocks.length) > 2 && (
                        <div style={{
                          fontSize: 'var(--text-2xs)',
                          color: 'var(--color-text-light)',
                          textAlign: 'center'
                        }}>
                          +{(dayBookings.length + dayBlocks.length) - 2}
                        </div>
                      )}
                    </div>
                  )
                ) : (
                  // Desktop: Show full booking/block cards
                  <div className="flex flex-col gap-xs">
                    <ReminderChips reminders={dayReminders} limit={1} />
                    {/* Block cards first - using orange color scheme */}
                    {dayBlocks.slice(0, 1).map((block: OperatorBlockRow) => {
                      const operatorInitials = block.operator_name ? getInitials(block.operator_name) : null;
                      return (
                        <div
                          key={`block-${block.id}`}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            padding: '4px 6px',
                            background: BLOCK_STYLE.bg,
                            borderLeft: `3px solid ${BLOCK_STYLE.border}`,
                            borderRadius: 'var(--radius-sm)',
                            fontSize: 'var(--text-xs)',
                            overflow: 'hidden',
                            whiteSpace: 'nowrap',
                            gap: '4px'
                          }}
                          title={`Blocked: ${block.reason || 'No reason'} - ${block.operator_name}${block.is_all_day ? ' (All Day)' : ''}`}
                        >
                          {operatorInitials && (
                            <span style={{
                              background: BLOCK_STYLE.border,
                              color: 'var(--color-white)',
                              width: '14px',
                              height: '14px',
                              borderRadius: '50%',
                              fontSize: '0.45rem',
                              fontWeight: 600,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0
                            }}>{operatorInitials}</span>
                          )}
                          <span style={{ color: BLOCK_STYLE.color, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {block.reason || 'Blocked'}
                          </span>
                        </div>
                      );
                    })}
                    {/* Booking cards */}
                    {dayBookings.slice(0, dayBlocks.length > 0 ? 2 : 3).map((booking: CalendarBookingRow) => {
                      const status = (STATUS_CONFIG[booking.status] || STATUS_CONFIG.pending)!;
                      const territoryCode = getTerritoryCode(booking.territory_name);
                      const operatorInitials = Array.isArray(booking.operator_names) && booking.operator_names.length > 0
                        ? getInitials(booking.operator_names[0])
                        : null;
                      const brandColour = booking.is_pos ? POS_GOLD : 'var(--color-primary)';
                      return (
                        <div
                          key={booking.id}
                          onClick={(e) => { e.stopPropagation(); handleViewBooking(booking); }}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            padding: '4px 6px',
                            background: status.bg,
                            // Thick brand-coloured left accent — same role as the
                            // top banner on Day / Week, transposed to a vertical bar.
                            // Status colour stays on the card via background tint.
                            borderLeft: `10px solid ${brandColour}`,
                            borderRadius: 'var(--radius-sm)',
                            fontSize: 'var(--text-xs)',
                            cursor: 'pointer',
                            overflow: 'hidden',
                            whiteSpace: 'nowrap',
                            gap: '4px'
                          }}
                          title={`${booking.booking_reference} - ${booking.customer_name}${booking.is_pos ? ' [POS]' : ''} (${booking.territory_name || 'Unknown'})`}
                        >
                          <span className="fw-600">{booking.start_time}</span>
                          <span style={{ color: 'var(--color-text)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {booking.customer_name?.split(' ')[0] || 'Unknown'}
                          </span>
                          {territoryCode && (
                            <span style={{
                              background: 'var(--color-bg)',
                              color: 'var(--color-sidebar-hover)',
                              padding: '0 3px',
                              borderRadius: 'var(--radius-sm)',
                              fontSize: 'var(--text-2xs)',
                              fontWeight: 600
                            }}>{territoryCode}</span>
                          )}
                          {operatorInitials && (
                            <span style={{
                              background: 'var(--color-primary-bg, #f3e8ff)',
                              color: 'var(--color-primary, #772cbb)',
                              width: '14px',
                              height: '14px',
                              borderRadius: '50%',
                              fontSize: '0.45rem',
                              fontWeight: 600,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0
                            }}>{operatorInitials}</span>
                          )}
                        </div>
                      );
                    })}
                    {(dayBookings.length + dayBlocks.length) > 3 && (
                      <div style={{
                        fontSize: 'var(--text-xs)',
                        color: 'var(--color-text-light)',
                        textAlign: 'center'
                      }}>
                        +{(dayBookings.length + dayBlocks.length) - 3} more
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Shared Legend */}
      <CalendarLegend isMobile={isMobile} showBlocks={showBlocks} calendarFilters={calendarFilters} showReminders={reminders.on} />

      {/* Mobile hint */}
      {isMobile && (
        <div className="cal-mobile-hint">Tap a day to see booking details</div>
      )}
    </div>
  );
}
